import {
  Outlet,
  RootRoute,
  Route,
  Router,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from './components/AppShell';
import { LoginPage } from './modules/auth/LoginPage';
import { DashboardPage } from './modules/dashboard/DashboardPage';
import { SettingsPage } from './modules/settings/SettingsPage';
import { InventoryPage } from './modules/products/InventoryPage';
import { WarehousesPage } from './modules/warehouses/WarehousesPage';
import { PosScreen } from './modules/pos/PosScreen';
import { OrdersListPage } from './modules/os/OrdersListPage';
import { NewOrderPage } from './modules/os/NewOrderPage';
import { OrderDetailPage } from './modules/os/OrderDetailPage';
import { AccountingPage } from './modules/accounting/AccountingPage';
import { PayrollPage } from './modules/payroll/PayrollPage';
import { UsersPage } from './modules/users/UsersPage';
import { CustomersPage } from './modules/customers/CustomersPage';
import { CustomerDetailPage } from './modules/customers/CustomerDetailPage';
import { CashDrawerPage } from './modules/cash-drawer/CashDrawerPage';
import { StockTakePage } from './modules/stock-take/StockTakePage';
import { InvoicesPage } from './modules/invoices/InvoicesPage';
import { AuditLogPage } from './modules/audit/AuditLogPage';
import { supabase } from './lib/supabase';

const rootRoute = new RootRoute({
  component: () => <Outlet />,
});

const loginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

async function requireAuth() {
  const { data } = await supabase.auth.getSession();
  if (!data.session) {
    throw redirect({ to: '/login' });
  }
}

const authedLayout = new Route({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: requireAuth,
  component: AppShell,
});

const dashboardRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/',
  component: DashboardPage,
});

const settingsRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/settings',
  component: SettingsPage,
});

const posRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/pos',
  component: PosScreen,
});
const ordersRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/orders',
  component: OrdersListPage,
});
const orderNewRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/orders/new',
  component: NewOrderPage,
});
const orderDetailRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/orders/$id',
  component: OrderDetailPage,
});
const inventoryRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/inventory',
  component: InventoryPage,
});
const warehousesRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/warehouses',
  component: WarehousesPage,
});
const accountingRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/accounting',
  component: AccountingPage,
});
const payrollRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/payroll',
  component: PayrollPage,
});
const usersRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/users',
  component: UsersPage,
});
const customersRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/customers',
  component: CustomersPage,
});
const customerDetailRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/customers/$id',
  component: CustomerDetailPage,
});
const cashDrawerRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/cash-drawer',
  component: CashDrawerPage,
});
const stockTakeRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/stock-take',
  component: StockTakePage,
});
const invoicesRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/invoices',
  component: InvoicesPage,
});
const auditRoute = new Route({
  getParentRoute: () => authedLayout,
  path: '/audit',
  component: AuditLogPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedLayout.addChildren([
    dashboardRoute,
    posRoute,
    ordersRoute,
    orderNewRoute,
    orderDetailRoute,
    inventoryRoute,
    warehousesRoute,
    accountingRoute,
    payrollRoute,
    invoicesRoute,
    cashDrawerRoute,
    stockTakeRoute,
    customersRoute,
    customerDetailRoute,
    usersRoute,
    auditRoute,
    settingsRoute,
  ]),
]);

export const router = new Router({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
